import { type IdentifierRecord, type ScanAnalyticsDailyDocument } from '@myplayer/schema';
import { IconAlertCircle, IconAuditLog, IconDomains, IconQrCode, IconRefresh } from '@myplayer/ui';
import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useAuth } from './auth-context.js';
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardIcon,
  CardTitle,
  EmptyState,
  ErrorBanner,
  SegmentedControl,
  Skeleton,
  Tag,
} from './components/ui/primitives.js';
import {
  callGetWorkspaceScanAnalyticsFunction,
  type GetWorkspaceScanAnalyticsCallablePayload,
  type GetWorkspaceScanAnalyticsCallableResult,
} from './firebase.js';
import {
  aggregateOverviewScanAnalytics,
  buildDayWindowList,
  formatShortUtcDayLabel,
  getTodayUtcIsoDay,
  isAdminCdnEnabled,
  type OverviewTimeWindow,
} from './overview-analytics-helpers.js';
import { formatPlainAdminError } from './strings.js';

const WINDOW_OPTIONS: readonly { readonly days: OverviewTimeWindow; readonly label: string }[] = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export interface ProductScanOverviewCardProps {
  readonly selectedRecord: IdentifierRecord;
  readonly isControlled?: boolean | undefined;
  readonly initialRollupResult?: GetWorkspaceScanAnalyticsCallableResult | undefined;
  readonly initialDailyRecords?: readonly ScanAnalyticsDailyDocument[] | undefined;
  readonly defaultWindowDays?: OverviewTimeWindow | undefined;
  readonly referenceDay?: string | undefined;
  readonly onFetchAnalytics?:
    | ((
        payload: GetWorkspaceScanAnalyticsCallablePayload,
      ) => Promise<GetWorkspaceScanAnalyticsCallableResult>)
    | undefined;
}

function ShareBarRow(p: {
  readonly label: string;
  readonly item: { readonly scans: number; readonly sharePct: number };
}): ReactElement {
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-2 text-[12px]">
        <span className="truncate font-medium text-fg">{p.label}</span>
        <span className="shrink-0 font-semibold text-fg tabular-nums">
          {p.item.scans.toLocaleString()} ({p.item.sharePct}%)
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-pill bg-brand-wash">
        <div
          className="h-full rounded-pill bg-brand"
          style={{ width: `${String(Math.max(4, p.item.sharePct))}%` }}
        />
      </div>
    </div>
  );
}

export function ProductScanOverviewCard(props: ProductScanOverviewCardProps): ReactElement {
  const {
    selectedRecord,
    isControlled = false,
    initialRollupResult,
    initialDailyRecords,
    defaultWindowDays = 7,
    referenceDay,
    onFetchAnalytics,
  } = props;
  const { session } = useAuth();
  const [windowDays, setWindowDays] = useState<OverviewTimeWindow>(defaultWindowDays);
  const effectiveReferenceDay = useMemo(() => referenceDay ?? getTodayUtcIsoDay(), [referenceDay]);
  const currentWindowDays = useMemo(
    () => buildDayWindowList(windowDays, effectiveReferenceDay),
    [windowDays, effectiveReferenceDay],
  );
  const windowFromDay = currentWindowDays[0] ?? effectiveReferenceDay;
  const windowToDay = currentWindowDays[currentWindowDays.length - 1] ?? effectiveReferenceDay;
  const targetOrgId = selectedRecord.orgId ?? session?.orgId ?? '';
  const targetWorkspaceId = selectedRecord.workspaceId ?? session?.activeWorkspaceId ?? '';
  const targetIdentifierKey = selectedRecord.identifierKey;
  const hasInitialAnalyticsSeed =
    initialRollupResult !== undefined || initialDailyRecords !== undefined;
  const shouldSkipImplicitFetch =
    isControlled && !hasInitialAnalyticsSeed && onFetchAnalytics === undefined;
  const [rollupResult, setRollupResult] = useState<GetWorkspaceScanAnalyticsCallableResult | null>(
    initialRollupResult ?? null,
  );
  const [analyticsLoading, setAnalyticsLoading] = useState<boolean>(
    !hasInitialAnalyticsSeed && !shouldSkipImplicitFetch && session !== null,
  );
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);
  const fetchScopeKey = `${targetOrgId}::${targetWorkspaceId}::${targetIdentifierKey}::${windowFromDay}::${windowToDay}`;

  const loadProductAnalytics = useCallback(async (): Promise<void> => {
    if (
      hasInitialAnalyticsSeed ||
      shouldSkipImplicitFetch ||
      !session ||
      !targetOrgId ||
      !targetWorkspaceId
    ) {
      setRollupResult(hasInitialAnalyticsSeed ? (initialRollupResult ?? null) : null);
      setAnalyticsLoading(false);
      return;
    }
    setAnalyticsLoading(true);
    setAnalyticsError(null);
    try {
      const fetchFn = onFetchAnalytics ?? callGetWorkspaceScanAnalyticsFunction;
      setRollupResult(
        await fetchFn({
          orgId: targetOrgId,
          workspaceId: targetWorkspaceId,
          identifierKey: targetIdentifierKey,
          fromDay: windowFromDay,
          toDay: windowToDay,
        }),
      );
    } catch (err) {
      setAnalyticsError(
        formatPlainAdminError(err, 'Unable to load scan analytics for this product.'),
      );
    } finally {
      setAnalyticsLoading(false);
    }
  }, [
    fetchScopeKey,
    hasInitialAnalyticsSeed,
    initialRollupResult,
    onFetchAnalytics,
    session,
    shouldSkipImplicitFetch,
    targetIdentifierKey,
    targetOrgId,
    targetWorkspaceId,
    windowFromDay,
    windowToDay,
  ]);
  useEffect(() => {
    void loadProductAnalytics();
  }, [loadProductAnalytics]);
  const productDailyRecords = useMemo(() => {
    if (initialDailyRecords === undefined) return undefined;
    return initialDailyRecords.filter(
      (rec) =>
        rec.identifierKey === targetIdentifierKey &&
        (!rec.workspaceId || !targetWorkspaceId || rec.workspaceId === targetWorkspaceId),
    );
  }, [initialDailyRecords, targetIdentifierKey, targetWorkspaceId]);
  const analytics = useMemo(
    () =>
      aggregateOverviewScanAnalytics({
        ...(rollupResult !== null ? { rollupResult } : {}),
        ...(productDailyRecords !== undefined ? { dailyRecords: productDailyRecords } : {}),
        identifiers: [selectedRecord],
        windowDays,
        referenceDay: effectiveReferenceDay,
      }),
    [rollupResult, productDailyRecords, selectedRecord, windowDays, effectiveReferenceDay],
  );
  const cdnEnabled = isAdminCdnEnabled();
  const kpiItems = [
    {
      testId: 'product-kpi-total-scans',
      label: `Scans (${windowDays} days)`,
      value: analyticsLoading ? '—' : analytics.totalScans.toLocaleString(),
      valCls: 'text-[20px]',
    },
    {
      testId: 'product-kpi-avg-daily-scans',
      label: 'Daily average',
      value: analyticsLoading ? '—' : `${analytics.averageDailyScans.toLocaleString()}/day`,
      valCls: 'text-[20px]',
    },
    ...(cdnEnabled
      ? [
          {
            testId: 'product-kpi-cache-hit-rate',
            label: 'Fast-edge hit rate',
            value: analyticsLoading ? '—' : `${String(analytics.cacheHitRatePct)}%`,
            valCls: 'text-[20px]',
          },
        ]
      : []),
    {
      testId: 'product-kpi-peak-day',
      label: 'Peak day',
      value:
        analyticsLoading || !analytics.peakDay
          ? '—'
          : `${analytics.peakDay.scans.toLocaleString()} (${formatShortUtcDayLabel(analytics.peakDay.day)})`,
      valCls: 'text-[16px]',
    },
  ];

  return (
    <Card data-testid="product-scan-overview-card">
      <CardHeader>
        <div className="flex min-w-0 items-center gap-3">
          <CardIcon>
            <IconAuditLog />
          </CardIcon>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <CardTitle>Scan overview</CardTitle>
              <span className="text-[12px] text-fg-secondary tabular-nums">
                ({analytics.fromDay} to {analytics.toDay} UTC)
              </span>
            </div>
            <CardDescription>
              QR code scans and scanner breakdown for GTIN {selectedRecord.primaryKey}
            </CardDescription>
          </div>
        </div>

        <div className="flex shrink-0 flex-wrap items-center gap-2">
          <SegmentedControl
            role="group"
            ariaLabel="Product scan analytics time range"
            data-testid="product-scan-window-control"
            value={windowDays}
            onChange={setWindowDays}
            items={WINDOW_OPTIONS.map((opt) => ({
              value: opt.days,
              label: opt.label,
              testId: `product-scan-window-${String(opt.days)}`,
            }))}
          />
          <Button
            type="button"
            size="icon"
            variant="secondary"
            data-testid="product-scan-refresh-button"
            aria-label="Refresh product scan analytics"
            title="Refresh product scan analytics"
            onClick={() => void loadProductAnalytics()}
          >
            <IconRefresh />
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Product Scan KPI strip */}
        <div
          data-testid="product-kpi-strip"
          className={`grid gap-3 ${cdnEnabled ? 'grid-cols-2 sm:grid-cols-4' : 'grid-cols-1 sm:grid-cols-3'}`}
        >
          {kpiItems.map((item) => (
            <div
              key={item.testId}
              data-testid={item.testId}
              className="rounded-control border border-subtle bg-surface-subtle p-3"
            >
              <div className="text-[12px] font-medium text-fg-secondary">{item.label}</div>
              <div className={`mt-1 ${item.valCls} font-bold leading-tight text-fg tabular-nums`}>
                {item.value}
              </div>
            </div>
          ))}
        </div>

        {analyticsError !== null && (
          <ErrorBanner
            title="Could not load product scan analytics"
            message={analyticsError}
            nextStep="Check your connection and retry."
            onRetry={() => void loadProductAnalytics()}
          />
        )}

        {analyticsLoading ? (
          <div data-testid="product-scan-analytics-skeleton" className="space-y-3">
            <Skeleton className="h-40 w-full" />
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          </div>
        ) : analytics.totalScans === 0 ? (
          <EmptyState
            data-testid="product-no-scans-state"
            icon={<IconQrCode />}
            title="No scans yet"
            description={`No QR code scans were recorded for this product in the last ${String(windowDays)} days.`}
          />
        ) : (
          <div className="space-y-4">
            {/* Daily scans bar chart */}
            <div
              data-testid="product-scans-chart"
              className="rounded-control border border-subtle p-3.5"
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-[13px] font-semibold text-fg">
                  Scans over time ({windowDays} days)
                </span>
                {analytics.peakDay !== null && (
                  <Tag variant="outline" size="sm">
                    Peak: {analytics.peakDay.scans.toLocaleString()} on{' '}
                    {formatShortUtcDayLabel(analytics.peakDay.day)}
                  </Tag>
                )}
              </div>

              <div aria-hidden="true" className="flex h-36 items-end gap-1.5 pt-3">
                {analytics.series.map((pt) => (
                  <div
                    key={pt.day}
                    className="group relative flex h-full flex-1 flex-col items-center justify-end"
                    title={`${pt.shortLabel}: ${pt.scans.toLocaleString()} scans`}
                  >
                    <div
                      className="w-full rounded-t-[4px] bg-brand transition-opacity group-hover:opacity-85"
                      style={{ height: `${String(pt.heightPct)}%` }}
                    />
                  </div>
                ))}
              </div>

              <div
                aria-hidden="true"
                className="mt-2 flex items-center justify-between border-t border-subtle pt-2 text-[11px] text-fg-muted tabular-nums"
              >
                <span>{analytics.series[0]?.shortLabel}</span>
                <span>{analytics.series[Math.floor(analytics.series.length / 2)]?.shortLabel}</span>
                <span>{analytics.series[analytics.series.length - 1]?.shortLabel}</span>
              </div>

              <div className="relative h-0 w-0 overflow-hidden">
                <table className="sr-only" data-testid="product-scans-sr-table">
                  <caption>
                    Daily QR code scans for GTIN {selectedRecord.primaryKey} over the last{' '}
                    {windowDays} days ({analytics.fromDay} to {analytics.toDay} UTC)
                  </caption>
                  <thead>
                    <tr>
                      <th scope="col">Date (UTC)</th>
                      <th scope="col">Scans</th>
                    </tr>
                  </thead>
                  <tbody>
                    {analytics.series.map((pt) => (
                      <tr key={pt.day}>
                        <td>{pt.day}</td>
                        <td>{pt.scans}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Top Countries & Device Split */}
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div
                data-testid="product-top-countries"
                className="space-y-2.5 rounded-control border border-subtle p-3.5"
              >
                <div className="flex items-center gap-2 text-fg-secondary">
                  <IconDomains />
                  <span className="text-[13px] font-semibold text-fg">Top countries</span>
                </div>
                {analytics.topCountries.length === 0 ? (
                  <p className="text-[12px] text-fg-secondary">No country breakdown recorded.</p>
                ) : (
                  analytics.topCountries.map((c) => (
                    <ShareBarRow key={c.countryCode} item={c} label={c.countryLabel} />
                  ))
                )}
              </div>
              <div
                data-testid="product-device-split"
                className="space-y-2.5 rounded-control border border-subtle p-3.5"
              >
                <div className="flex items-center gap-2 text-fg-secondary">
                  <IconQrCode />
                  <span className="text-[13px] font-semibold text-fg">Device split</span>
                </div>
                {analytics.deviceSplit.length === 0 ? (
                  <p className="flex items-center gap-2 text-[12px] text-fg-secondary">
                    <IconAlertCircle />
                    <span>No device category breakdown recorded.</span>
                  </p>
                ) : (
                  analytics.deviceSplit.map((dev) => (
                    <ShareBarRow key={dev.category} item={dev} label={dev.label} />
                  ))
                )}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
